// Radar widget (Players canvas) — a position-template spider chart: the player's
// per-90 metrics scaled between fixed bounds, drawn as a polygon. Old Catalyst's
// Radars tab, as a Players-canvas widget (`player-radar`). No `soccer-vis` (§7B):
// hand-drawn SVG. All the geometry/scaling lives in the pure, unit-tested
// `radar.ts` (reused by the upcoming Passing widget). Consumes player + comp + season.
//
// Template selection: auto-picked from the player's most-played position (a
// /player-stats call grouped by position_id), overridable via the dropdown.

import { useMemo, useState } from "react";
import { useMetricCategories } from "../data/stats.js";
import { usePlayerStats, usePlayerPositionMinutes } from "../data/players.js";
import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { RADAR_TEMPLATES } from "./radarTemplates.js";
import { pickTemplateByPosition, type RadarMetric } from "./radar.js";
import RadarChart from "./RadarChart.js";

export default function RadarWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cats = useMetricCategories(true); // per-90 catalogue (labels + outputType)
  const posMins = usePlayerPositionMinutes(playerId, competitionId, seasonId);
  const [manualId, setManualId] = useState<string | null>(null);

  // key → { shortName, outputType } for label + percentage detection.
  const info = useMemo(() => {
    const map: Record<string, { shortName: string; outputType?: string | null }> = {};
    for (const c of cats.data ?? [])
      for (const s of c.subcategories)
        for (const m of s.playerMetrics) map[m.key] = { shortName: m.shortName, outputType: m.outputType };
    return map;
  }, [cats.data]);

  // Only the POSITION templates are offered here. The skill-based "passing"
  // template (positions: []) is intentionally excluded — it has a dedicated
  // Passing Radar widget that scopes it to the player's position group and adds a
  // peer overlay. Offering it here too showed the SAME template with different
  // (overall vs positional) numbers, which was confusing.
  const positionTemplates = RADAR_TEMPLATES.filter((t) => t.positions.length > 0);
  const auto = posMins.data ? pickTemplateByPosition(RADAR_TEMPLATES, posMins.data.rows) : null;
  const template =
    positionTemplates.find((t) => t.id === manualId) ?? auto ?? positionTemplates[0]!;
  const keys = template.metrics.map((m) => m.key);

  // Filter the per-90 stats to the template's positions, so the numbers match old
  // Catalyst (which scopes the radar to the minutes played in those positions).
  const stats = usePlayerStats(playerId, competitionId, seasonId, keys, template.positions);

  const dropdown = (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.inkMuted }}>
      Template
      <select
        value={template.id}
        onChange={(e) => setManualId(e.target.value)}
        style={{
          background: theme.input,
          color: theme.ink,
          border: `1px solid ${theme.borderInput}`,
          borderRadius: 6,
          padding: "4px 8px",
          fontSize: 12,
        }}
      >
        {positionTemplates.map((t) => (
          <option key={t.id} value={t.id}>
            {t.label}
          </option>
        ))}
      </select>
      {manualId === null && auto && <span style={{ color: theme.inkFaint }}>· auto</span>}
    </label>
  );

  if (playerId === null) return <Shell head={dropdown}><Msg>Search for a player above.</Msg></Shell>;
  if (competitionId === null || seasonId === null)
    return <Shell head={dropdown}><Msg>Pick a competition and season above.</Msg></Shell>;
  if (cats.isPending || stats.isPending) return <Shell head={dropdown}><Msg>Loading radar…</Msg></Shell>;
  if (cats.isError) return <Shell head={dropdown}><Msg danger>Couldn't load metrics. {String(cats.error)}</Msg></Shell>;
  if (stats.isError) return <Shell head={dropdown}><Msg danger>Couldn't load stats. {String(stats.error)}</Msg></Shell>;

  const row = stats.data.rows[0];
  // With the position filter on, an empty row means the player logged no minutes
  // in this template's positions (not that they have no stats) — say so.
  if (!row)
    return (
      <Shell head={dropdown}>
        <Msg>No minutes in the {template.label} positions this season — try another template.</Msg>
      </Shell>
    );

  const metrics: RadarMetric[] = template.metrics.map((m) => ({
    key: m.key,
    label: info[m.key]?.shortName ?? m.key,
    bounds: m.bounds,
    isPercentage: info[m.key]?.outputType === "percentage",
  }));

  return (
    <Shell head={dropdown}>
      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center" }}>
        <RadarChart metrics={metrics} row={row} ariaLabel={`${template.label} radar`} />
      </div>
    </Shell>
  );
}

function Shell({ head, children }: { head: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
      <div>{head}</div>
      {children}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
